"use client";

import React, { useEffect } from "react";
import { 
  MapPin, 
  Calendar, 
  Clock, 
  ArrowLeft, 
  MessageCircle, 
  Info, 
  CircleCheck, 
  Share2,
  Camera,
  ChevronRight
} from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";

const parseGallery = (galleryData: any): string[] => {
  if (!galleryData) return [];
  if (Array.isArray(galleryData)) return galleryData;
  if (typeof galleryData === "string") return [galleryData];
  return [];
};

function SingleTourContent() {
  const { posts, loading, error } = usePosts();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-24 space-y-8 animate-pulse">
        <div className="h-8 w-32 bg-muted rounded-md" />
        <div className="h-12 w-3/4 bg-muted rounded-md" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <div className="h-[500px] bg-muted rounded-xl" />
            <div className="h-40 bg-muted rounded-xl" />
          </div>
          <div className="h-96 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <h2 className="text-3xl font-heading font-bold text-destructive mb-4">Error Loading Tour</h2>
        <p className="text-lg font-sans text-muted-foreground mb-8">We couldn't load the tour details. Please try again.</p>
        <Link to="/SelectedToursGridArchitecturePage">
          <Button variant="outline" className="rounded-[4px] border-primary text-primary hover:bg-primary hover:text-white">
            Return to Tours
          </Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <h2 className="text-3xl font-heading font-bold mb-4">Tour Not Found</h2>
        <Link to="/SelectedToursGridArchitecturePage">
          <Button className="rounded-[4px] bg-primary text-white">Browse All Tours</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const customFields = post.customFields || {};
  
  const overview = customFields.overview || post.content || "No overview available.";
  const itinerary = customFields.itinerary ? customFields.itinerary.split('\n').filter((line: string) => line.trim() !== '') : [];
  const highlights = customFields.highlights || "";
  const galleryImages = parseGallery(customFields.gallery);
  const whatsappLink = customFields.whatsapp_link || "#";
  const mainImage = post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553131/img/tour_placeholder_default.png";

  return (
    <div className="relative min-h-screen bg-white pb-24 lg:pb-32">
      {}
      <div className="relative pt-12 lg:pt-20 pb-16 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="mb-8">
            <Link to="/SelectedToursGridArchitecturePage" className="inline-flex items-center text-sm font-sans font-medium text-muted-foreground hover:text-primary transition-colors group">
              <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
              Back to Collections
            </Link>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap gap-2 mb-6">
                {post.categories?.map((cat, i) => (
                  <Badge key={i} variant="outline" className="rounded-full border-primary/20 text-primary font-sans px-4 py-1">
                    {cat.name}
                  </Badge>
                ))}
              </div>
              <h1 className="text-5xl lg:text-7xl font-heading font-bold tracking-tighter text-primary leading-[1.1] mb-6">
                {post.title}
              </h1>
              {post.excerpt && (
                <div 
                  className="text-xl lg:text-2xl font-sans text-muted-foreground leading-relaxed max-w-3xl" 
                  dangerouslySetInnerHTML={{ __html: post.excerpt }} 
                />
              )}
            </div>
            <div className="lg:col-span-4 flex justify-end">
              <Button variant="outline" size="icon" className="rounded-full border-muted text-muted-foreground hover:border-primary hover:text-primary transition-all" aria-label="Share">
                <Share2 className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
        {}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/5 -skew-x-12 translate-x-1/2 -z-0" />
      </div>

      <div className="container mx-auto px-4">
        {}
        <div className="relative -mt-4 mb-20">
          <div className="rounded-xl overflow-hidden shadow-2xl">
            <AspectRatio ratio={21 / 9}>
              <img 
                src={mainImage} 
                alt={post.title} 
                className="w-full h-full object-cover"
              />
            </AspectRatio>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {}
          <div className="lg:col-span-8 space-y-20">
            
            {}
            <section id="overview" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-8">
                <span className="h-px w-12 bg-accent" />
                <h2 className="text-3xl font-heading font-bold text-primary">The Experience</h2>
              </div>
              <div className="prose prose-lg max-w-none text-muted-foreground font-sans leading-[1.6] whitespace-pre-wrap text-lg">
                {overview}
              </div>
            </section>

            <Separator className="bg-border/50" />

            {}
            {itinerary.length > 0 && (
              <section id="itinerary" className="scroll-mt-24">
                <div className="flex items-center gap-4 mb-12">
                  <span className="h-px w-12 bg-accent" />
                  <h2 className="text-3xl font-heading font-bold text-primary">Your Journey</h2>
                </div>
                
                <div className="space-y-12">
                  {itinerary.map((item: string, i: number) => (
                    <div key={i} data-index={i} className="flex gap-8 group">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full border-2 border-accent flex items-center justify-center text-primary font-heading font-bold text-lg shrink-0 group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                          {String(i + 1).padStart(2, '0')}
                        </div>
                        {i !== itinerary.length - 1 && (
                          <div className="w-px h-full bg-border my-4" />
                        )}
                      </div>
                      <div className="pb-4 pt-2">
                        <p className="text-xl font-sans text-foreground leading-[1.6]">{item}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {}
            {galleryImages.length > 0 && (
              <section id="gallery" className="scroll-mt-24 pt-8">
                <div className="flex items-center gap-4 mb-12">
                  <span className="h-px w-12 bg-accent" />
                  <h2 className="text-3xl font-heading font-bold text-primary">Visual Narrative</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {galleryImages.map((img: string, i: number) => (
                    <div 
                      key={i} 
                      data-index={i}
                      className={`rounded-lg overflow-hidden shadow-md group ${i % 3 === 0 ? 'md:col-span-2' : ''}`}
                    >
                      <AspectRatio ratio={i % 3 === 0 ? 21/9 : 4/3}>
                        <img 
                          src={img} 
                          alt={`Gallery image ${i + 1}`} 
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      </AspectRatio>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {}
          <div className="lg:col-span-4">
            <div className="sticky top-24 space-y-8">
              
              {}
              {highlights && (
                <Card className="bg-accent/10 border-none shadow-none rounded-xl overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-3 text-xl font-heading font-bold text-primary">
                      <CircleCheck className="w-6 h-6 text-accent" />
                      Highlights
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="prose prose-sm font-sans text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {highlights}
                    </div>
                  </CardContent>
                </Card>
              )}

              {}
              <Card className="border border-border shadow-xl bg-white rounded-xl overflow-hidden">
                <CardContent className="p-8 space-y-8">
                  <div className="space-y-3 text-center lg:text-left">
                    <h3 className="text-2xl font-heading font-bold text-primary">Design Your Trip</h3>
                    <p className="text-base font-sans text-muted-foreground leading-relaxed">
                      Connect with our specialists for a bespoke itinerary tailored to your preferences.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <Link to={whatsappLink} newTab className="w-full block">
                      <Button size="lg" className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-base h-14 rounded-[4px] shadow-lg shadow-green-500/20">
                        <MessageCircle className="w-6 h-6 mr-3 fill-current" />
                        Inquire via WhatsApp
                      </Button>
                    </Link>
                    
                    <Link to="/ContactPage" className="w-full block">
                      <Button variant="outline" size="lg" className="w-full border-primary text-primary hover:bg-primary hover:text-white font-bold text-base h-14 rounded-[4px]">
                        Custom Inquiry
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 text-xs font-sans text-muted-foreground uppercase tracking-widest">
                    <Clock className="w-3 h-3" />
                    Response within 60 minutes
                  </div>
                </CardContent>
              </Card>

              {}
              <div className="p-8 bg-primary text-white rounded-xl space-y-4">
                <h4 className="font-heading font-bold text-xl">Private & Exclusive</h4>
                <p className="font-sans text-white/80 text-sm leading-relaxed">
                  All our tours are private. We focus on boutique experiences that avoid the crowds and reveal the authentic soul of Israel.
                </p>
                <div className="pt-2 flex items-center gap-2 text-accent font-bold text-sm">
                  Learn about our philosophy <ChevronRight className="w-4 h-4" />
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-md border-t border-border z-50 lg:hidden shadow-[0_-10px_30px_-10px_rgba(0,0,0,0.1)]">
        <div className="container mx-auto">
          <Link to={whatsappLink} newTab className="block">
            <Button size="lg" className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-bold h-14 rounded-[4px]">
              <MessageCircle className="w-5 h-5 mr-3 fill-current" />
              Book this Experience
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function SingleTourTemplateSection() {
  const currentWpQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "tour", p: 1 }; 

  if (!currentWpQueryParams.post_type) {
    currentWpQueryParams.post_type = "tour";
  }

  const wpQuery = new WP_Query(currentWpQueryParams);

  return (
    <section id="single-tour-template-section" className="bg-white">
      <PostsProvider wp_query={wpQuery}>
        <SingleTourContent />
      </PostsProvider>
    </section>
  );
}